/* eslint-disable @typescript-eslint/no-explicit-any */
import CryptoJS from "crypto-js";
// import crypto from "crypto";
import { replaceAccentLetters } from "./prepare-text";

export interface CrosswordEntry {
  clue: string;
  answer: string;
}

export function wordToKey(w: CrosswordEntry) {
  return [w.clue, w.answer].join(" - ");
}

export function wordToKeyNoSpace(w: CrosswordEntry) {
  return wordToKey(w).replace(/[ -]/g, "");
}

export function canSwapWord(w: CrosswordEntry) {
  const oneWord = /[^a-zåäö-]/;
  return !oneWord.test(w.clue) && !oneWord.test(w.answer);
}

export function swapWord(w: CrosswordEntry) {
  return { answer: w.clue, clue: w.answer };
}

export function parseText(row: string) {
  const clean = (t: string) => t.replace(/[\u00ad]/g, "").replace(/[\u00a0]/g, " ");
  const rs = row.trim().split(/ (?:- )?/);
  const clue = clean(rs.slice(0, -1).join(" ").toLocaleLowerCase());
  const answer = clean(rs.at(-1)!.toLocaleLowerCase());
  return { clue, answer } as CrosswordEntry;
}

const CONSONANTS = "bcdfghjklmnpqrstvwxz";
const REG_EXP_TEST_CONSONANTS = CONSONANTS.split("").map((d) => new RegExp(`${d}{3}`, "g"));
const DOUBLE_CONSONANTS = CONSONANTS.split("").map((d) => `${d}${d}`);
const CONSONANT_DICT = Object.fromEntries(CONSONANTS.split("").map((d) => [d, true]));

export function isConsonant(c: string) {
  return CONSONANT_DICT[c[0]];
}

export function replaceThreeConsonants(w: string) {
  for (let i = 0; i < CONSONANTS.length; i++) {
    const t = REG_EXP_TEST_CONSONANTS[i];
    const r = DOUBLE_CONSONANTS[i];
    w = w.replace(t, r);
  }
  return w;
}

export function cleanText(t: string) {
  return replaceAccentLetters(t)
    .toLocaleLowerCase()
    .replace(/[^a-zåäö]/g, " ")
    .split(/[ -]/)
    .filter(Boolean)
    .join(" ");
}

function getFrags(t: string, nLetters = 4, isStart = true) {
  const ts = t.split(" ").filter((d) => d.length >= nLetters);
  const res: Record<string, boolean> = {};
  for (const d of ts) {
    for (let i = nLetters; i <= d.length; i++) {
      res[isStart ? d.slice(0, i) : d.slice(-i)] = true;
    }
  }
  return res;
}

export function isRepeat(w: CrosswordEntry, nLetters = 4) {
  const cleanClue = cleanText(w.clue);
  const cfs = getFrags(cleanClue, nLetters, true);
  const afs = getFrags(cleanText(w.answer), nLetters, true);
  const ps = Object.keys(afs);
  if (ps.some((d) => cfs[d])) return true;
  const afe = getFrags(cleanText(w.answer), nLetters, false);
  const cps = cleanClue.split(" ").filter((d: string) => d.length >= nLetters);
  if (cps.some((d: string) => afe[d])) return true;
  return false;
}

export const SPLIT = (template: TemplateStringsArray) =>
  template
    .join("")
    .split("\n")
    .map((d) => d.trim())
    .filter(Boolean);

function genderFromEnding(n: string) {
  if (
    /(lle|tte|ie|git|ild|ice|ike|dil|un|ise|tel|vor|ine|bet|lill|rud|borg|iet|fer|dit|mor|itt|ig|idi|ane|anne|evi|one|ese)$/.test(
      n
    )
  ) {
    return "female";
  }
  if (
    /(ot|an|ik|ons|red|var|elm|on|ur|ar|min|rd|st|old|nny|und|and|ger|rne|ert|rnt|lf|ing|nk|te|ld|id|rry|örn|ten)$/.test(
      n
    )
  ) {
    return "male";
  }
  if (/[a]$/.test(n)) return "female";
  if (/[ofkrtlps]$/.test(n)) return "male";
  return null;
}

export function calcGender(n: string, knownNames: Record<string, string>) {
  const g = knownNames[n];
  if (g) return g;
  return genderFromEnding(n);
}

export function genetiveName(name: string) {
  return `${name}${/[sxz]$/i.test(name) ? "" : "s"}`;
}

export function distinct(arr: string[]) {
  return [...new Set(arr)];
}

export function sum(arr: number[]) {
  return arr.reduce((a, b) => a + b, 0);
}

export function shuffle(array: any[], random: () => number = Math.random) {
  const n = array.length;
  for (let i = 0; i < n - 1; i++) {
    const r = Math.floor(random() * (n - 1 - i));
    const i1 = i + 1 + r;
    const t = array[i1];
    array[i1] = array[i];
    array[i] = t;
  }
  return array;
}

export function ROWS(rs: any[]) {
  return rs
    .join("")
    .trim()
    .split("\n")
    .map((d) => d.trim())
    .filter(Boolean);
}

export function md5(data: string, digits: number | null = null): string {
  const res = CryptoJS.MD5(data).toString(); // eslint-disable-line new-cap
  // const res = crypto.createHash("md5").update(data).digest("hex");
  if (digits === null) return res;
  return res.slice(-digits);
}

export function safeBase64(data: string): string {
  const res = Buffer.from(data, "hex").toString("base64");
  return res.replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
}

export function hash(data: string): string {
  return safeBase64(md5(data, 12));
}

export function createId(prefix: string, seed: string) {
  const res = hash(seed);
  if (prefix === "") return res;
  if (prefix.startsWith("/") || prefix.endsWith("_")) return `${prefix}${res}`;
  return `${prefix}-${res}`;
}

export function groupBy<T>(
  xs: T[],
  keyFun = (d: T, _i?: number) => String(d),
  valueFun = (d: T, _i?: number): unknown => d
) {
  const dict = Object.fromEntries(xs.map((d) => [keyFun(d), [] as any[]]));
  xs.forEach((d: T, i: number) => dict[keyFun(d, i)].push(valueFun(d, i)));
  return dict;
}
